import cx from "classnames";
import { Map, MapSettings } from "common/Map";
import { LiveLogLine } from "common/Map/lines/LiveLogLine";
import { Marker } from "common/Map/Marker";
import { RouteLine } from "common/Map/RouteLine";
import { PhotoClusters } from "common/Maplet/PhotoClusters";
import { withMetricUnits } from "common/units/MetricUnitsContext";
import * as userActions from "lib/api/currentUser";
import { getSliceBounds } from "lib/routeUtils";
import _ from "lodash";
import React, { Fragment, useReducer } from "react";
import { connect } from "react-redux";
import { LiveLogGraph } from "./LiveLogGraph";
import styles from "./MapPanel.scss";
import { initialState, reducer, types } from "./reducer";

const MetricAwareMap = withMetricUnits(Map);

// Copied from RSP
const SG_NORMAL = 1;
const getSgHeight = (collapseState) => {
  switch (collapseState) {
    case SG_NORMAL:
    default:
      const MAX_HEIGHT = 280;
      const MIN_HEIGHT = 120;
      const proportional = window.innerHeight * 0.25;
      return _.clamp(proportional, MIN_HEIGHT, MAX_HEIGHT);
  }
};

const LiveLogMapSettings = MapSettings({
  showDistanceMarkers: true,
});

const userPrefMappings = {
  showDistanceMarkers: "route_viewer_enable_distance_markers",
  metricUnits: "metric_units",
  scrollWheelZoom: "route_viewer_enable_scrollwheel_zoom",
  showPOIs: "route_viewer_show_pois",
};

const getMapSettingsForUser = _.memoize((user) => {
  const prefs = Object.entries(userPrefMappings)
    .map(([clientKey, serverKey]) => [clientKey, user.preferences.get(serverKey, undefined)])
    .filter(([clientKey, value]) => value !== undefined);

  return LiveLogMapSettings.merge(prefs);
});

export function _MapPanel({
  liveLog,
  trip,
  troute,
  user,
  pointsOfInterest,
  photos,
  mobile,
  className,
  fullscreen,
  currentUser,
  setUserPreference,
  unstarted,
}) {
  const [mapPanelState, mapPanelDispatch] = useReducer(reducer, initialState);

  const worldBounds = [
    { lat: 90, lng: 180 },
    { lat: -90, lng: -180 },
  ];
  const suggestedBounds = unstarted ? worldBounds : getSuggestedBounds(trip, troute);

  const combinedRawPoints = combineRawPoints(trip, troute, liveLog);

  const highlightPoint = mapPanelState.get("highlightPoint");
  const snapLatLng = highlightPoint && combinedRawPoints[highlightPoint.index];

  let mapHeight;
  if ((mobile && fullscreen) || (!trip && !troute) || unstarted) {
    mapHeight = "100%";
  } else if (mobile) {
    mapHeight = "320px";
  } else {
    mapHeight = `calc(100% - ${getSgHeight(SG_NORMAL)}px)`;
  }

  const toggleSetting = (settingKey, newSettings) => {
    // This api will error for anonymous users ¯\_(ツ)_/¯
    // But following the pattern in RSP, we set it anyways
    setUserPreference(currentUser.id, userPrefMappings[settingKey], newSettings.get(settingKey));
  };

  const defaultMapSettings = getMapSettingsForUser(currentUser);

  return (
    <div className={cx(styles.MapPanel, className, mobile && styles.mobile, fullscreen && styles.fullscreen)}>
      <MetricAwareMap
        style={{ zIndex: 0 }}
        experimental
        showToolbar
        height={mapHeight}
        pois={pointsOfInterest}
        suggestedBounds={suggestedBounds}
        highlightPoint={mapPanelState.highlightPoint}
        onPointHighlighted={(point) => mapPanelDispatch({ type: types.HIGHLIGHT_POINT, point })}
        onToggleSetting={toggleSetting}
        mapSettings={defaultMapSettings}
      >
        {/* Route needs to possibly go first, so it gets displayed below the livelog trip line */}
        {!unstarted && (
          <Fragment>
            {troute && <RouteLine route={troute} showStartMarker={false} />}
            {trip && trip.trackPoints.length > 0 && (
              <LiveLogLine
                liveLog={liveLog}
                user={user}
                trip={trip}
                showDistanceMarkers={defaultMapSettings.showDistanceMarkers}
              />
            )}
            {snapLatLng && <Marker position={{ lat: snapLatLng.y, lng: snapLatLng.x }} icon="default" />}
            {photos && <PhotoClusters troute={trip.set("photos", photos)} clusterStrategy="trip" />}
          </Fragment>
        )}
      </MetricAwareMap>
      {!mobile && !unstarted && (trip || troute) && (
        <LiveLogGraph
          rawPoints={combinedRawPoints}
          height={getSgHeight(SG_NORMAL)}
          mapPanelState={mapPanelState}
          mapPanelDispatch={mapPanelDispatch}
        />
      )}
    </div>
  );
}

const getSuggestedBounds = _.memoize((trip, troute) => {
  const tripPoints = (trip && trip.trackPoints) || [];
  const troutePoints = (troute && troute.trackPoints) || [];
  return getSliceBounds({
    trackPoints: tripPoints.concat(troutePoints),
  });
});

const combineRawPoints = _.memoize((trip, troute, liveLog) => {
  const tripPoints = (trip && trip.rawPoints) || [];
  const troutePoints = (troute && troute.rawPoints) || [];

  if (isNavigatingTRoute(liveLog)) {
    return tripPoints
      .concat(troutePoints.filter((p) => p.d > liveLog.distanceAlong).map((p) => Object.assign({}, p, { k: true })))
      .map((p) => _.omit(p, "t")); // strip timestamps, since we're munging a trip and troute, timestamps don't make sense, and also confuse samplegraph
  }

  return tripPoints;
});

const isNavigatingTRoute = (liveLog) => {
  return liveLog.assetId && typeof liveLog.distanceAlong === "number" && liveLog.distanceAlong >= 0;
};

const mapStateToProps = (state) => ({
  currentUser: state.currentUser,
});

const mapDispatchToProps = {
  setUserPreference: userActions.setUserPreference,
};

export const MapPanel = connect(mapStateToProps, mapDispatchToProps)(_MapPanel);
